import { Key, PublicNonces } from '@borislav.itskov/schnorrkel.js';
import { zodResolver } from '@hookform/resolvers/zod';
import { UseQueryResult } from '@tanstack/react-query';
import { message, notification } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button, LoadingButton } from 'src/components/ui/button';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from 'src/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/components/ui/select';
import { useSigner } from 'src/contexts/SignerContext';
import { getCountdown } from 'src/helpers/date';
import { useVaultAddress } from 'src/hooks/useVaultAddress';
import { CountdownState } from 'src/interfaces/common';
import { formatSchnorrForContract } from 'src/services/auth/key-pair';
import { useErc20Balance } from 'src/services/blockchain/get-erc20-balance';
import {
  buildWithdrawSchnorrVariables,
  useSchnorrWithdrawContract,
} from 'src/services/blockchain/withdraw-schnorr';
import { GetSupportedPairsResponse } from 'src/services/token/get-supported-tokens';
import { useCancelWithdrawalTrade } from 'src/services/user/canel-withdrawal-trade';
import {
  ClaimWithdrawalTradeReqData,
  useClaimWithdrawalTrade,
} from 'src/services/user/claim-withdrawal-trade';
import {
  CombineSignaturesResponse,
  useCombineSignatures,
} from 'src/services/user/combine-signatures';
import { useGetWithdrawalAmountTrade } from 'src/services/user/get-withdrawal-amount-trade';
import { useWaitForTransactionReceipt } from 'wagmi';
import { z } from 'zod';
import { useAppTranslation } from 'src/i18n/hooks';
import i18n from 'src/i18n';
import TokenLogo from 'src/components/ui/TokenLogo';
import { useAppSelector } from 'src/store';
import { THEME_MODE } from 'src/interfaces/theme';

interface WithdrawableProps {
  supportedTokensQuery: UseQueryResult<GetSupportedPairsResponse, Error>;
  chainId: number;
  onWithdrawSettle?: () => void;
  selectedToken?: string;
}

const withdrawFormSchema = z.object({
  token: z.string(),
  withdrawAmount: z
    .string()
    .refine((val) => val === '' || (!isNaN(parseFloat(val)) && parseFloat(val) > 0), {
      message: i18n.t('trade:positiveAmountNumber'),
    }),
});
type WithdrawFormValues = z.infer<typeof withdrawFormSchema>;

const Withdrawable = (props: WithdrawableProps) => {
  let activeNotification: string | null = null;
  const { supportedTokensQuery, chainId, onWithdrawSettle, selectedToken } = props;
  const { getPublicNonces, getPublicKey, multiSignMessage, signer } = useSigner();
  const { t } = useAppTranslation('trade');
  const userTheme = useAppSelector((state) => state.theme);

  const [isLoading, setIsLoading] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const initialSelectedToken = selectedToken ?? supportedTokensQuery.data?.at?.(0)?.address;
  const [tokenTicker, setTokenTicker] = useState('USDT');

  const withdrawSchnorrContractMutation = useSchnorrWithdrawContract();
  const { mutateAsync: combineSigsMutate, isPending } = useCombineSignatures();

  const [countdown, setCountdown] = useState<CountdownState>({
    days: '00',
    hours: '00',
    minutes: '00',
    seconds: '00',
  });
  const [endCountdown, setEndCountdown] = useState(true);

  const form = useForm<WithdrawFormValues>({
    resolver: zodResolver(withdrawFormSchema),
    defaultValues: {
      token: initialSelectedToken,
    },
  });

  const { data: withdrawableData, refetch: refetchWithdrawableData } = useGetWithdrawalAmountTrade({
    tokenID: tokenTicker,
    chainID: chainId,
  });

  const selectedTokenAddress = form.watch('token');
  const erc20BalanceQuery = useErc20Balance({
    tokenAddress: selectedTokenAddress as `0x${string}`,
  });

  const vaultAddress = useVaultAddress();
  const cancelWithdrawalMutation = useCancelWithdrawalTrade();
  const claimMutation = useClaimWithdrawalTrade();
  const refetchErc20Balance = erc20BalanceQuery.refetch;

  const waitForWithdrawalReceiptQuery = useWaitForTransactionReceipt({
    hash: withdrawSchnorrContractMutation.data,
    confirmations: 1,
    query: {
      enabled: !!withdrawSchnorrContractMutation.data,
    },
  });

  useEffect(() => {
    if (withdrawSchnorrContractMutation.data) {
      withdrawSchnorrContractMutation.reset();
    }
  }, [chainId]);

  const handleEndCountdown = () => {
    setEndCountdown(true);
    setCountdown({
      days: '00',
      hours: '00',
      minutes: '00',
      seconds: '00',
    });
  };

  const updateCountDown = () => {
    const now = moment.utc();
    if (
      withdrawableData &&
      withdrawableData?.pendingWithdrawals?.length &&
      withdrawableData.pendingWithdrawals.length > 0
    ) {
      const targetDate = moment.utc(withdrawableData.pendingWithdrawals[0].availableAt);
      const diff = targetDate.diff(now);
      if (diff > 0) {
        setCountdown(getCountdown(diff));
        setEndCountdown(false);
        return true;
      } else {
        handleEndCountdown();
        return false;
      }
    } else {
      handleEndCountdown();
    }
  };

  const handlePreviousSignature = async (e: any) => {
    const schnorr = formatSchnorrForContract(e as CombineSignaturesResponse);
    try {
      await withdrawSchnorrContractMutation.writeContractAsync(
        buildWithdrawSchnorrVariables({
          combinedPublicAddress: schnorr.combinedPublicKey,
          schnorr,
          vaultAddress,
        }),
        {
          onSuccess: handleWithdrawSuccess,
          onError: (error: any) => {
            if (isUserRejection(error)) {
              handleUserRejection();
            } else {
              handleContractError(error);
            }
          },
        },
      );
    } catch (error: any) {
      if (error?.message?.includes('insufficient funds')) {
        message.error(t('withdrawModal.notEnoughFunds'));
      } else if (isUserRejection(error)) {
        handleUserRejection();
      } else {
        handleContractError(error);
      }
    }
  };

  // Updated function
  const handleNewSignature = async (e: any) => {
    const nonces = {
      kPublic: Key.fromHex(e.publicNonce.kPublic),
      kTwoPublic: Key.fromHex(e.publicNonce.kTwoPublic),
    };
    const publicKeyData = Key.fromHex(e.publicKey);
    const publicKeys = [publicKeyData, getPublicKey()];
    const platformNonce = getPublicNonces();
    const publicNonces = [nonces, platformNonce];

    const signature = multiSignMessage(
      e.signatureData,
      publicKeys as Key[],
      publicNonces as PublicNonces[],
      signer!,
    );

    const combinedSig = {
      signature: signature?.signature.toHex(),
      signatureID: e.signatureID,
    };

    try {
      await combineSigsMutate(combinedSig!, {
        onSuccess: async (data) => {
          const schnorr = formatSchnorrForContract(data);
          try {
            await withdrawSchnorrContractMutation.writeContractAsync(
              buildWithdrawSchnorrVariables({
                combinedPublicAddress: schnorr.combinedPublicKey,
                schnorr,
                vaultAddress,
              }),
              {
                onSuccess: handleWithdrawSuccess,
                onError: (error: any) => {
                  if (isUserRejection(error)) {
                    handleUserRejection();
                  } else {
                    handleContractError(error);
                  }
                },
              },
            );
          } catch (error: any) {
            if (isUserRejection(error)) {
              handleUserRejection();
            } else {
              handleContractError(error);
            }
          }
        },
        onError: (error: any) => {
          if (error?.message?.includes('insufficient funds')) {
            message.error(t('withdrawModal.notEnoughFunds'));
          } else if (isUserRejection(error)) {
            handleUserRejection();
          } else {
            handleCombineSigsError(error);
          }
        },
      });
    } catch (error: any) {
      if (error?.message?.includes('insufficient funds')) {
        message.error(t('withdrawModal.notEnoughFunds'));
      } else if (isUserRejection(error)) {
        handleUserRejection();
      } else {
        handleCombineSigsError(error);
      }
    }
  };

  // Updated function
  const handleWithdrawSuccess = (txData: any) => {
    // console.log('onSuccess tx', txData);
    setIsLoading(false);
    // showNotification('success', 'Withdrawal Successful', 'Your withdrawal has been successful');
  };

  useEffect(() => {
    const interval = setInterval(() => {
      updateCountDown();
      refetchWithdrawableData();
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [withdrawableData]);

  const handleContractError = (error: any) => {
    console.error('Contract error:', error);
    if (isUserRejection(error)) {
      handleUserRejection();
    } else {
      showErrorNotification(
        t('withdrawModal.contractError'),
        t('withdrawModal.contractErrorDesc'),
      );
    }
    setIsLoading(false);
  };

  const handleCombineSigsError = (error: any) => {
    console.error('Combine signatures error:', error);
    if (isUserRejection(error)) {
      handleUserRejection();
    } else {
      showErrorNotification(
        t('withdrawModal.signatureError'),
        t('withdrawModal.signatureErrorDesc'),
      );
    }
    setIsLoading(false);
  };

  const isUserRejection = (error: any): boolean => {
    const rejectionMessages = [
      t('userDeniedTxSignature'),
      t('userRejectedTheTx'),
      t('userCanceled'),
      t('metamaskTxSignature'),
      t('userRejectedTx'), // New addition
    ];

    return rejectionMessages.some(
      (msg) =>
        error.message?.toLowerCase().includes(msg.toLowerCase()) ||
        (error.details && error.details.toLowerCase().includes(msg.toLowerCase())) ||
        (error.cause &&
          error.cause.message &&
          error.cause.message.toLowerCase().includes(msg.toLowerCase())), // New addition
    );
  };

  const handleUserRejection = () => {
    showNotification(
      'info',
      t('withdrawModal.transactionCancelled'),
      t('withdrawModal.transactionCancelledDesc'),
    );
    setIsLoading(false);
  };

  const showNotification = (
    type: 'success' | 'error' | 'info',
    message: string,
    description: string,
  ) => {
    if (activeNotification) {
      notification.destroy(activeNotification);
    }

    activeNotification = `${type}-${Date.now()}`;
    notification[type]({
      message,
      description,
      placement: 'bottomRight',
      key: activeNotification,
      onClose: () => {
        activeNotification = null;
      },
    });
  };

  const showErrorNotification = (message: string, description: string) => {
    showNotification('error', message, description);
  };

  useEffect(() => {
    if (waitForWithdrawalReceiptQuery.status === 'pending') {
      return;
    }
    if (waitForWithdrawalReceiptQuery.status === 'success') {
      notification.success({
        message: t('withdrawModal.withdrawalSuccessful'),
        description: t('withdrawModal.withdrawalSuccessfulDesc'),
        placement: 'bottomRight',
        style: { fontFamily: 'Poppins' },
      });
      onWithdrawSettle?.();
      refetchErc20Balance();
    }
  }, [waitForWithdrawalReceiptQuery.status, refetchErc20Balance]);

  useEffect(() => {
    if (supportedTokensQuery?.data) {
      supportedTokensQuery.data.find(({ address, token_id }) => {
        if (selectedTokenAddress === address) {
          setTokenTicker(token_id);
        }
      });
    }
  }, [selectedTokenAddress, supportedTokensQuery]);

  const handleCancelWithdraw = async () => {
    if (withdrawableData && withdrawableData?.pendingWithdrawals?.length > 0) {
      setLoadingCancel(true);
      await cancelWithdrawalMutation
        .mutateAsync(withdrawableData.pendingWithdrawals[0].id)
        .then(() => {
          // form.reset();
          // handleEndCountdown();
          setTimeout(() => {
            notification.success({
              message: t('withdrawModal.cancelWithdrawalSuccessful'),
              description: t('withdrawModal.cancelWithdrawalSuccessfulDesc'),
              placement: 'bottomRight',
              style: { fontFamily: 'Poppins' },
            });
            refetchWithdrawableData();
            setLoadingCancel(false);
          }, 1000);
        })
        .catch((error) => {
          showErrorNotification(t('withdrawModal.cancelWithdrawalError'), t('withdrawModal.cancelWithdrawalErrorDesc'));
        })
        .finally(() => {
          setLoadingCancel(false);
        });
    }
  };

  const handleWithdrawConfirm = async (data: any) => {
    setIsLoading(true);
    if (
      withdrawableData &&
      withdrawableData?.pendingWithdrawals?.length > 0 &&
      withdrawableData?.availableAmount > 0
    ) {
      const withdrawReqData: ClaimWithdrawalTradeReqData = {
        tokenID: tokenTicker,
        balance: parseFloat(withdrawableData?.availableAmount.toString() ?? '0'),
        chainID: chainId,
        publicKey: getPublicKey()?.toHex()!,
        publicNonce: {
          kPublic: getPublicNonces()?.kPublic.toHex()!,
          kTwoPublic: getPublicNonces()?.kTwoPublic.toHex()!,
        },
        id: withdrawableData.pendingWithdrawals[0].id,
      };

      claimMutation
        .mutateAsync(withdrawReqData)
        .then((res) => {
          if (res.usePreviousSignature) {
            handlePreviousSignature(res);
          } else {
            handleNewSignature(res);
          }
        })
        .catch((error) => {
          notification.error({
            message: t('withdrawModal.withdrawableError'),
            description: t('withdrawModal.withdrawableErrorDesc'),
            placement: 'bottomRight',
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
      notification.error({
        message: t('withdrawModal.withdrawableError'),
        description: t('withdrawModal.withdrawableMustBeGreaterThanZero'),
        placement: 'bottomRight',
      });
    }
  };

  return (
    <div>
      <FormProvider {...form}>
        <form>
          <FormField
            control={form.control}
            name="token"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="text-black dark:text-white">{t('withdrawModal.asset')}</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-white dark:bg-gray-800 border hover:border-blue-300 transition-colors">
                      <SelectValue placeholder={t('withdrawModal.selectToken')} />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent
                    className={`z-[1000] asset-dropdown ${userTheme === THEME_MODE.DARK ? 'asset-dropdown-dark' : ''}`}
                    position="popper"
                    sideOffset={5}
                    style={{ position: 'relative' }}
                  >
                    {supportedTokensQuery.data?.map(({ address, token_id }, index) => (
                      <SelectItem key={index} value={address}>
                        <div className="flex items-center gap-2">
                          <TokenLogo tokenId={token_id} size="sm" />
                          <span className={`font-medium ${userTheme === THEME_MODE.DARK ? 'text-white' : 'text-black'}`}>{token_id}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="flex gap-4 my-4 sm:my-3 justify-between">
            <p className="text-black dark:text-white">{t('withdrawModal.availableAt')}</p>
            <p className="text-black dark:text-white">
              {countdown.days}d {countdown.hours}h {countdown.minutes}m {countdown.seconds}s
            </p>
          </div>
          <div className="flex gap-4 my-4 sm:my-3 justify-between">
            <p className="text-black dark:text-white">{t('withdrawModal.withdrawable')}</p>
            <p className="text-black dark:text-white font-semibold">
              {withdrawableData?.availableAmount} {tokenTicker}
            </p>
          </div>
          <div className="flex gap-4 my-4 sm:my-3 justify-between">
            <p className="text-black dark:text-white">{t('withdrawModal.pendingWithdraw')}</p>
            <p className="text-black dark:text-white font-semibold">
              {endCountdown
                ? `0 ${tokenTicker}`
                : withdrawableData?.pendingWithdrawals?.length
                  ? `${withdrawableData.pendingWithdrawals[0].amount} ${tokenTicker}`
                  : `0 ${tokenTicker}`}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 my-6 sm:my-4 sm:justify-end">
            {withdrawableData && (
              <Button
                onClick={handleCancelWithdraw}
                className="w-full sm:w-fit py-3 sm:py-4 px-4 sm:px-6 text-white font-medium rounded-lg border-2 border-transparent bg-black dark:bg-black [background:linear-gradient(#000,#000)_padding-box,linear-gradient(90deg,#0920CF_0%,#5415DE_50%,#CB01F7_100%)_border-box] hover:opacity-90 disabled:opacity-50"
                type="button"
                loading={cancelWithdrawalMutation.isPending}
                disabled={
                  withdrawSchnorrContractMutation.isPending ||
                  waitForWithdrawalReceiptQuery.isFetching ||
                  isPending ||
                  isLoading ||
                  loadingCancel ||
                  !(withdrawableData && withdrawableData?.pendingWithdrawals?.length > 0)
                }
              >
                {t('withdrawModal.cancelRequestWithdraw')}
              </Button>
            )}

            <LoadingButton
              onClick={handleWithdrawConfirm}
              type="button"
              className="text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 bg-[linear-gradient(90deg,#0920CF_0%,#5415DE_50%,#CB01F7_100%)] hover:opacity-90 disabled:opacity-50"
              loading={
                withdrawSchnorrContractMutation.isPending ||
                waitForWithdrawalReceiptQuery.isFetching ||
                isPending ||
                isLoading ||
                loadingCancel
              }
            >
              {t('withdrawModal.withdraw')}
            </LoadingButton>
          </div>
        </form>
      </FormProvider>
    </div>
  );
};

export default Withdrawable;
